import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

import noop from 'lodash/noop';

import Button from '@ott/button';
import FirstErrorShower from '@ott/first-error-shower';
import CrossIcon from '@ott/icon-cross';
import Input from '@ott/input';
import { l10n } from '@ott/l10n';
import Modal, { CONTENT_SIZE } from '@ott/modal';
import {
  selectors as virtualCardSelectors,
  hooks as virtualCardHooks,
} from '@ott/shared-virtual-card-logic';
import { limiter } from '@ott/superagent';
import Tooltip from '@ott/tooltip';
import { phoneNumber, removeNonNumbers } from '@ott/transforms';
import { makePhoneNumberFormatter, phoneNumberFormatterMask } from '@ott/utility-formatters';

import type { UpgradeParams } from '../../../UpgradeFormModal';
import { useKM } from '../../hooks/useKM';
import { ErrorResponse } from '../../types';
import ResendSms from './blocks/ResendSMS';
import { FORM_FIELDS, FormValues } from './helpers/constants';
import useFormSubmit from './hooks/useFormSubmit';
import useReleaseForm from './hooks/useReleaseForm';

import styles from './ReleaseFormModal.scss';

type Props = {
  withQuickAuthorization: boolean;
  upgradePayload: UpgradeParams;
  preventUserCloseModal?: boolean;
  onError: (value: ErrorResponse) => void;
  onSuccess: VoidFunction;
  onClose: VoidFunction;
};

const {
  virtualCardFlow: { useVirtualCardFlow },
} = virtualCardHooks;

const MODAL_CONTENT_OPTIONS = {
  size: CONTENT_SIZE.SMALL,
  title: l10n('virtualCard.releaseForm.title'),
  className: styles.modalContent,
};

const phoneNumberFormatter = makePhoneNumberFormatter();

const ReleaseFormModal = (props: Props) => {
  const {
    upgradePayload,
    withQuickAuthorization = false,
    preventUserCloseModal,
    onError = noop,
    onSuccess = noop,
    onClose = noop,
  } = props;

  const formRef = useRef<HTMLFormElement>(null);
  const [isPinSent, setIsPinSent] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const { sendKM } = useKM();

  const {
    auth,
    auth: { error, isLoading: isAuthLoading, isAuthorized },
  } = useVirtualCardFlow();

  const cardRelease = useSelector(virtualCardSelectors.getCardRelease);
  const cardUpgrade = useSelector(virtualCardSelectors.getCardUpgrade);
  const pinCode = useSelector(virtualCardSelectors.getPinCode);

  const isLoading =
    isAuthLoading || cardRelease?.loading || pinCode?.loading || cardUpgrade?.loading;

  const { releaseCard, getPinCode } = useFormSubmit({
    isSubmitted: isFormSubmitted,
    setIsPinSent,
    onError,
    onSuccess,
    onClose,
    withQuickAuthorization,
    upgradePayload,
  });

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      if (isPinSent) {
        sendKM('Virtual_Card_Enter_Pin_Code_Entered');
        await releaseCard(values);
      } else {
        sendKM('Virtual_Card_Enter_Phone_Continue_Clicked');
        await getPinCode(values);
      }
    },
    [isPinSent, releaseCard, getPinCode]
  );

  const { time, control, isSubmitted, errors, onFormSubmit, reset, getValues, handleInvalidPin } =
    useReleaseForm({
      isPinSent,
      onFormSubmit: handleSubmit,
      serverError: cardRelease?.errorData,
    });

  const handleResendSms = useCallback(async () => {
    const { phone } = getValues();
    await getPinCode({ phone });
  }, [getValues]);

  const resetForm = () => {
    setIsPinSent(false);

    const { phone } = getValues();
    reset({
      phone,
      pin: '',
    });

    if (!isAuthorized) {
      auth.regReset?.();
    }
  };

  useEffect(() => {
    resetForm();

    return () => setIsFormSubmitted(false);
  }, []);

  useEffect(() => {
    if (isSubmitted !== isFormSubmitted) {
      setIsFormSubmitted(isSubmitted);
    }
  }, [isSubmitted]);

  useEffect(() => {
    if (isFormSubmitted) {
      sendKM(
        isPinSent ? 'Virtual_Card_Enter_Pin_transition' : 'Virtual_Card_Enter_Phone_transition'
      );
    }
  }, [isPinSent, isFormSubmitted]);

  const handleClose = useCallback(() => {
    sendKM('Virtual_Card_Issued_Success_Close_No_Uprid_Press');
    onClose();
  }, [onClose]);

  const tooltipContent = (
    <span className={styles.tooltip}>{l10n('virtualCard.releaseForm.reset')}</span>
  );

  return (
    <Modal
      specificLocators="release-modal"
      isOpen={true}
      fullScreenOnMobile={true}
      contentOptions={MODAL_CONTENT_OPTIONS}
      preventUserClose={preventUserCloseModal}
      onClose={handleClose}
    >
      <div className={styles.wrapper} data-locator="virtual-card-release-form">
        <div className={styles.guide}>
          {isPinSent
            ? l10n('virtualCard.releaseForm.guide.sent')
            : l10n('virtualCard.releaseForm.guide.start')}
        </div>

        {error && (
          <div className={styles.error} data-locator="auth-error-message">
            {error}
          </div>
        )}

        <form className={styles.form} ref={formRef} onSubmit={onFormSubmit}>
          <FirstErrorShower
            time={time}
            show={isSubmitted}
            errors={errors}
            container={formRef.current}
          />

          <div className={styles.controls}>
            <Controller
              name={FORM_FIELDS.PHONE}
              control={control}
              render={({ field, fieldState }) => (
                <div className={styles.phone}>
                  <Input
                    type="tel"
                    autoComplete="tel"
                    mask={phoneNumberFormatterMask(field.value)}
                    formatter={phoneNumberFormatter}
                    parser={phoneNumber}
                    isDisabled={isPinSent || isLoading}
                    label={l10n('virtualCard.releaseForm.placeholders.phone')}
                    className={styles.field}
                    specificLocators="release-card-phone"
                    hasError={Boolean(fieldState.error?.message)}
                    {...field}
                  />
                  {isPinSent && (
                    <Tooltip
                      content={tooltipContent}
                      triggerClassName={styles.icon}
                      isDisabledOnTouchDevice={true}
                    >
                      <button
                        data-locator="reset-form-button"
                        type="button"
                        className={styles.resetButton}
                        onClick={resetForm}
                      >
                        <CrossIcon />
                      </button>
                    </Tooltip>
                  )}
                </div>
              )}
            />

            {isPinSent && (
              <>
                <Controller
                  name={FORM_FIELDS.PIN}
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      type="text"
                      className={styles.field}
                      parser={removeNonNumbers}
                      isDisabled={isLoading}
                      label={l10n('virtualCard.releaseForm.placeholders.pin')}
                      specificLocators="release-card-pin"
                      hasError={Boolean(fieldState.error?.message)}
                      {...field}
                    />
                  )}
                />
                <ResendSms onClick={handleResendSms} />
              </>
            )}
          </div>

          <limiter.CaptchaContainer className={styles.captcha} name="enter-by-phone" />

          <div className={styles.button}>
            <Button
              type="submit"
              isLoading={isLoading}
              withFullWidth={true}
              specificLocators="virtual-card-release-form-action-button"
            >
              {l10n('virtualCard.releaseForm.action')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ReleaseFormModal;
